import { useEffect } from "react";

const SITE_NAME = "Pool Software Guide";
const DEFAULT_OG_IMAGE = "https://images.pexels.com/photos/1147124/pexels-photo-1147124.jpeg?auto=compress&w=1200&h=630&fit=crop";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (content == null || content === "") {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// title: page title (brand suffix appended). description, image (OG), jsonLd (object OR array → @graph),
// noindex: bool. canonicalPath: override path (defaults to current pathname, no query string).
export function useSEO({ title, description, jsonLd, image, noindex = false, canonicalPath }) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Best Pool Service Software Reviews`;
    document.title = fullTitle;

    const origin = window.location.origin;
    const canonical = origin + (canonicalPath || window.location.pathname);
    const ogImage = image || DEFAULT_OG_IMAGE;

    upsertMeta("name", "description", description || "");
    upsertLink("canonical", canonical);
    upsertMeta("name", "robots", noindex ? "noindex,follow" : "index,follow");

    // Open Graph
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description || "");
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", ogImage);

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description || "");
    upsertMeta("name", "twitter:image", ogImage);

    // JSON-LD (object or array → @graph)
    let script = document.getElementById("jsonld-schema");
    if (jsonLd) {
      const payload = Array.isArray(jsonLd)
        ? { "@context": "https://schema.org", "@graph": jsonLd }
        : jsonLd;
      if (!script) {
        script = document.createElement("script");
        script.id = "jsonld-schema";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(payload);
    } else if (script) {
      script.remove();
    }

    window.scrollTo(0, 0);
  }, [title, description, image, noindex, canonicalPath, JSON.stringify(jsonLd)]);
}

// Helper: build a BreadcrumbList schema node from [{name, path}] (path relative, last item = current page)
export function breadcrumb(items) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: origin + it.path,
    })),
  };
}
